/**
 * Token-economy sound cues — synthesized with the Web Audio API (no external
 * assets, no license issues, works offline). Two cues:
 *   • playChime()       — short, warm two-note bell when a token is earned
 *   • playCelebration() — ascending arpeggio + soft sparkle when the board fills
 *
 * Mute state is persisted in localStorage as `discrimina_token_sound_muted`
 * (default "off" → sounds enabled). All calls no-op if muted or if the browser
 * blocks audio (autoplay policy).
 */

const MUTE_KEY = "discrimina_token_sound_muted";

let _ctx = null;
function getCtx() {
  if (typeof window === "undefined") return null;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!_ctx) {
    try { _ctx = new AC(); } catch { return null; }
  }
  // Some browsers start suspended until user gesture — attempt resume.
  if (_ctx.state === "suspended") { _ctx.resume().catch(() => {}); }
  return _ctx;
}

export function isMuted() {
  if (typeof localStorage === "undefined") return false;
  return localStorage.getItem(MUTE_KEY) === "1";
}

export function setMuted(v) {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(MUTE_KEY, v ? "1" : "0");
}

function tone(ctx, freq, startAt, duration, { type = "sine", peak = 0.18, attack = 0.01, release = 0.15 } = {}) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, startAt);
  gain.gain.setValueAtTime(0, startAt);
  gain.gain.linearRampToValueAtTime(peak, startAt + attack);
  gain.gain.exponentialRampToValueAtTime(0.0001, startAt + duration + release);
  osc.connect(gain).connect(ctx.destination);
  osc.start(startAt);
  osc.stop(startAt + duration + release + 0.05);
}

/** Short cheerful "ding-dong" for each token earned. */
export function playChime() {
  if (isMuted()) return;
  const ctx = getCtx();
  if (!ctx) return;
  const now = ctx.currentTime;
  tone(ctx, 880, now,       0.09, { type: "triangle", peak: 0.22 }); // A5
  tone(ctx, 1318.5, now + 0.07, 0.14, { type: "triangle", peak: 0.18 }); // E6
}

/** Longer celebratory arpeggio when the board fills up. */
export function playCelebration() {
  if (isMuted()) return;
  const ctx = getCtx();
  if (!ctx) return;
  const now = ctx.currentTime;
  // Major arpeggio: C5 - E5 - G5 - C6
  const notes = [523.25, 659.25, 783.99, 1046.5];
  notes.forEach((f, i) => tone(ctx, f, now + i * 0.11, 0.16, { type: "triangle", peak: 0.22 }));
  // Sparkle: high shimmer sines on top
  [1568, 2093, 2637].forEach((f, i) => tone(ctx, f, now + 0.45 + i * 0.08, 0.12, { type: "sine", peak: 0.09 }));
  // Warm sustain — soft square adds body
  tone(ctx, 261.6, now, 0.55, { type: "sine", peak: 0.12, release: 0.4 });
}
